import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Loader2, Lock, Shield, CheckCircle2, AlertCircle, Download, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const PLANS = {
  buyer_yearly: { label: 'Buyer Yearly Subscription', base: 9999 },
  supplier_yearly: { label: 'Supplier Yearly Subscription', base: 2499 },
};

const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const planKey = searchParams.get('plan') || (user?.role === 'supplier' ? 'supplier_yearly' : 'buyer_yearly');
  const plan = PLANS[planKey] || PLANS.buyer_yearly;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [legalAccepted, setLegalAccepted] = useState({ terms_and_conditions: false, privacy_policy: false, refund_policy: false, data_governance: false });
  const allLegalAccepted = Object.values(legalAccepted).every(Boolean);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const base = plan.base;
  const gst = Math.round(base * 0.18 * 100) / 100;
  const total = Math.round((base + gst) * 100) / 100;

  useEffect(() => {
    axios.get(`${API}/payments/status`).then(r => setStatus(r.data)).catch(() => {});
  }, []);

  const startCheckout = async () => {
    if (!user) {
      toast.error('Please login to subscribe');
      navigate('/login?next=/checkout?plan=' + planKey);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/order`, { plan: planKey }, { withCredentials: true });
      if (!data.ok) {
        toast.error(data.message || 'Payments not yet active');
        setLoading(false);
        return;
      }
      const order = data.order;

      // Open Razorpay Checkout
      if (typeof window.Razorpay === 'undefined') {
        toast.error('Razorpay checkout failed to load. Please refresh the page.');
        setLoading(false);
        return;
      }

      const options = {
        key: status?.razorpay_key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'ProcureOne Ultra',
        description: plan.label + (status?.is_test_mode ? ' (TEST MODE)' : ''),
        order_id: order.id,
        prefill: { name: user.name, email: user.email },
        theme: { color: '#0A1F44' },
        handler: async (response) => {
          try {
            const verify = await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: planKey,
            }, { withCredentials: true });
            setSuccess(verify.data);
            toast.success('Payment successful! Invoice sent to your email.');
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          } finally { setLoading(false); }
        },
        modal: {
          ondismiss: () => { setLoading(false); toast('Checkout cancelled'); },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-card border border-border rounded-lg p-8 text-center" data-testid="checkout-success">
          <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-navy mb-2">Payment Successful!</h2>
          <p className="text-muted-foreground mb-6">
            Your <strong>{plan.label}</strong> is now active. A GST invoice (#{success.invoice_no}) has been emailed to <strong>{user?.email}</strong>.
          </p>
          <div className="bg-muted/40 rounded p-4 mb-5 text-left space-y-1 text-sm">
            <div className="flex justify-between"><span>Invoice No.</span><span className="font-mono">{success.invoice_no}</span></div>
            <div className="flex justify-between"><span>Amount Paid</span><span className="font-bold">₹{success.amount_paid?.toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between"><span>Subscription</span><span>{plan.label}</span></div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={`${API}/payments/invoice/${success.subscription_id}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded bg-navy text-white text-sm font-semibold inline-flex items-center justify-center" data-testid="download-invoice-btn">
              <Download className="h-4 w-4 mr-2" /> Download Invoice PDF
            </a>
            <button onClick={() => navigate(user?.role === 'supplier' ? '/supplier-dashboard' : '/buyer-dashboard')} className="px-4 py-2 rounded border border-input text-sm font-semibold">
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-2">
          <button onClick={() => navigate(-1)} className="text-white/70 hover:text-white inline-flex items-center" data-testid="checkout-back-btn">
            <ArrowLeft className="h-5 w-5 mr-2" /> Back
          </button>
          <div className="text-lg font-bold">Secure Checkout</div>
          <div className="text-xs text-white/70 inline-flex items-center"><Lock className="h-3 w-3 mr-1" /> 256-bit SSL</div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-10 max-w-2xl">
        {/* Test Mode Banner */}
        {status?.is_test_mode && (
          <div className="mb-5 p-3 rounded-lg bg-orange/15 border border-orange/40 flex items-start" data-testid="test-mode-banner">
            <AlertCircle className="h-5 w-5 text-orange mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-orange">
              <strong>TEST MODE</strong> · No real money will be charged. Use any test card from{' '}
              <a className="underline" target="_blank" rel="noopener noreferrer" href="https://razorpay.com/docs/payments/payments/test-card-upi-details/">Razorpay test cards</a>.
            </div>
          </div>
        )}

        {/* Payments disabled banner */}
        {status?.mode === 'disabled' && (
          <div className="mb-5 p-4 rounded-lg bg-yellow-50 border border-yellow-300" data-testid="payments-disabled-banner">
            <div className="font-bold text-yellow-900 mb-1">Payments Not Yet Active</div>
            <p className="text-sm text-yellow-800">Razorpay activation is in progress. You can pre-register your interest at <button onClick={() => navigate('/pre-register')} className="underline font-semibold">/pre-register</button> and we'll notify you when checkout opens.</p>
          </div>
        )}

        <div className="bg-card border border-border rounded-lg p-6 mb-5">
          <h1 className="text-2xl font-bold text-navy mb-1">{plan.label}</h1>
          <p className="text-sm text-muted-foreground mb-5">Annual subscription · Auto-renewal off · Cancel anytime</p>

          <div className="space-y-3 mb-5">
            <Row label="Subscription Fee" value={`₹${base.toLocaleString('en-IN')}.00`} />
            <Row label="CGST @ 9%" value={`₹${(gst / 2).toFixed(2)}`} />
            <Row label="SGST @ 9%" value={`₹${(gst / 2).toFixed(2)}`} />
            <div className="border-t pt-3"></div>
            <Row label="Total Payable" value={`₹${total.toLocaleString('en-IN')}`} bold testid="checkout-total" />
          </div>

          <div className="mt-4 p-3 rounded border border-border bg-muted/30" data-testid="legal-acceptance-block">
            <div className="text-xs font-bold text-navy mb-2">Mandatory Acceptance — required to proceed</div>
            {[
              ['terms_and_conditions', 'Terms & Conditions', '/terms-and-conditions'],
              ['privacy_policy', 'Privacy Policy', '/privacy-policy'],
              ['refund_policy', 'Refund & Cancellation Policy', '/terms-and-conditions#refund'],
              ['data_governance', 'Data Protection Policy', '/data-protection-policy'],
            ].map(([k, lbl, href]) => (
              <label key={k} className="flex items-center gap-2 text-xs text-foreground/80 py-0.5" data-testid={`legal-${k}-label`}>
                <input
                  type="checkbox"
                  checked={legalAccepted[k]}
                  onChange={(e) => setLegalAccepted(prev => ({ ...prev, [k]: e.target.checked }))}
                  data-testid={`legal-${k}-check`}
                />
                <span>I have read and accept the <a href={href} target="_blank" rel="noreferrer" className="underline text-orange font-semibold" data-testid={`legal-${k}-link`}>{lbl}</a> (v1.0)</span>
              </label>
            ))}
          </div>

          <button
            onClick={async () => {
              if (!allLegalAccepted) return toast.error('Please accept all four policies to continue');
              try {
                await axios.post(`${API}/legal/accept`, {
                  accepted_policies: Object.keys(legalAccepted),
                  context: 'subscription_checkout',
                }, { withCredentials: true });
              } catch (e) { toast.error('Legal acceptance failed'); return; }
              startCheckout();
            }}
            disabled={loading || status?.mode === 'disabled' || !allLegalAccepted}
            className="w-full px-4 py-3 rounded-md bg-gold hover:bg-orange text-navy hover:text-white font-bold text-sm transition-colors disabled:opacity-50 inline-flex items-center justify-center mt-3"
            data-testid="pay-now-btn"
          >
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing…</> : <><Lock className="h-4 w-4 mr-2" /> Pay ₹{total.toLocaleString('en-IN')} via Razorpay</>}
          </button>

          <p className="text-xs text-muted-foreground mt-4 inline-flex items-center">
            <Shield className="h-3 w-3 mr-1" /> Payments handled by Razorpay — PCI DSS Level 1 certified.
          </p>
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <p>• A GST invoice (PDF) will be emailed to {user?.email || 'your registered email'} immediately on success.</p>
          <p>• Founding Members get 24-month validity. Standard plans are 12 months.</p>
          <p>• For queries: info@plusultrainnovation.com</p>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, bold, testid }) => (
  <div className="flex justify-between text-sm" data-testid={testid}>
    <span className={bold ? 'font-bold text-navy' : 'text-muted-foreground'}>{label}</span>
    <span className={bold ? 'font-bold text-navy text-base' : ''}>{value}</span>
  </div>
);

export default Checkout;
